import React, { useContext, useEffect, useState, type FC, type PropsWithChildren } from 'react';

import { withImage, useExternalLogos } from '@lucid/hooks';
import { variant } from '@lucid/theme';
import styled from '@emotion/styled';
import kebabCase from 'lodash/kebabCase';

import Image from '../../image/image';
import LayoutContext from '../../context/layout-context';
import Box from '../../box/box';
import type { Lucid } from '../../../types/types';
import Icon from '../../icon/icon';

export type LogoSectionAspectRatioTypes = '1/1' | '4/3' | '5/3' | '16/9' | 'unset';

export interface LogoSectionProps {
  /** Sets the image aspect ratio */
  aspectRatio?: LogoSectionAspectRatioTypes;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets the logo height */
  height?: number | string;
  /** Sets the logo width */
  width?: number | string;
  /** Sets which logos should be displayed */
  logos?: string[] | Lucid.Logo.LanguagesSet;
  /** Sets the color of the logos */
  logosColor?: Lucid.Theme.Component.LogoSection.Color.VariantKeys;
  /** The available variants for the LogoSection component. */
  variant?: Lucid.Theme.Component.LogoSection.Layout.VariantKeys;
}

const StyledLogoSection = styled.ul(
  variant({
    prop: 'logosColor',
    scale: 'logoSection.color',
  }),
  variant({
    scale: 'logoSection.layout',
  }),
  ({ theme, isCarousel, shouldPauseAnimation, logosLength, isBoxes }) => [
    {
      label: 'logoSection',
      animationDuration: `calc(7s * ${isCarousel ? logosLength * 2 : logosLength})`,
    },
    shouldPauseAnimation && {
      animationPlayState: shouldPauseAnimation ? 'paused' : 'running',
    },
    isCarousel && {
      '&:nth-of-type(1)': {
        animationName: theme.keyframe.slideToLeft,
      },
      '&:nth-of-type(2)': {
        animationName: theme.keyframe.slideFromRight,
      },
    },
    isBoxes && {
      [theme.breakpoints.bp768.max]: {
        '&:nth-of-type(1)': {
          animationName: theme.keyframe.slideToLeft,
        },
        '&:nth-of-type(2)': {
          animationName: theme.keyframe.slideFromRight,
        },
      },
    },
  ],
);

const LogoItem: FC<{
  logo: Lucid.Directus.Generated.External_Logo;
  aspectRatio: string;
  height: number | string;
  width: number | string;
  variant?: Lucid.Theme.Component.LogoSection.Layout.VariantKeys;
}> = ({ logo, aspectRatio, height, width, variant }) => {
  const isBoxes = variant?.includes('boxes');

  return (
    <li
      css={theme => [
        {
          textAlign: 'center',
          justifyContent: 'center',
          flexBasis: 'auto',
          maxWidth: `${(height as number) / 4}%`,
          [theme.breakpoints.bp768.min]: {
            height: height,
          },
        },
        isBoxes && {
          backgroundColor: theme.colors.white,
        },
      ]}
      data-external-logo={logo.name}
    >
      <Image
        css={theme => ({
          aspectRatio: aspectRatio,
          objectFit: 'contain',
          width: '100%',
          maxHeight: isBoxes ? '100%' : height || '100%',
          [theme.breakpoints.bp768.min]: {
            aspectRatio: 'auto',
            width: isBoxes ? '100%' : width,
            height: isBoxes ? '100%' : height,
          },
        })}
        draggable={false}
        imageAlt={logo?.image?.translations?.[0]?.alt || ''}
        imageSrc={withImage(logo.image)}
      />
    </li>
  );
};

const LogoSection: FC<PropsWithChildren<LogoSectionProps>> = ({
  aspectRatio = '5/3',
  className,
  logos = ['hp', 'amazon', 'nbc', 'google', 'atlassian'],
  logosColor = 'original',
  width = 'auto',
  height = 70,
  variant = 'oneLine',
  ...rest
}) => {
  const { shortCode } = useContext(LayoutContext);
  const [shouldPauseAnimation, setShouldPauseAnimation] = useState(false);

  if (logos && (logos as Lucid.Logo.LanguagesSet)[shortCode]) {
    // logos is a LogoLanguagesSet and has logos for the current language
    logos = (logos as Lucid.Logo.LanguagesSet)[shortCode] as Lucid.Logo.Slug[];
  }

  if ((logos as Lucid.Logo.LanguagesSet).en) {
    // logos is a LogoLanguagesSet but needs to use english as fallback
    logos = (logos as Lucid.Logo.LanguagesSet).en as Lucid.Logo.Slug[];
  }

  const selectedLogos = useExternalLogos(logos as string[]);
  const isCarousel = variant.toLowerCase().includes('carousel') && selectedLogos.length >= 5;
  const isBoxes = variant.toLowerCase().includes('boxes');
  const logosToDisplay = isCarousel || isBoxes ? [...selectedLogos, ...selectedLogos] : selectedLogos;

  useEffect(() => {
    if (!isCarousel && variant.toLowerCase().includes('carousel')) setShouldPauseAnimation(true);
  }, [isCarousel, variant]);

  const sections = Array.from({ length: isCarousel ? 2 : 1 }, (_, i) => (
    <StyledLogoSection
      key={`logo-section-${i + 1}`}
      aria-hidden={!!i || undefined}
      className={className}
      isBoxes={isBoxes}
      isCarousel={isCarousel}
      logosColor={logosColor}
      logosLength={selectedLogos.length}
      shouldPauseAnimation={shouldPauseAnimation}
      variant={!isCarousel && variant.toLowerCase().includes('carousel') ? 'oneLine' : variant}
    >
      {logosToDisplay.map((logo, index) => (
        <LogoItem
          key={`logo-section-item-${logo.id}-${kebabCase(logo.name)}-${index + 1}`}
          aspectRatio={aspectRatio}
          height={height}
          logo={logo}
          variant={variant}
          width={width}
        />
      ))}
    </StyledLogoSection>
  ));

  return (
    <Box
      {...rest}
      css={{
        position: 'relative',
        '&:hover': {
          '> div': {
            clip: 'auto',
            clipPath: 'none',
          },
        },
      }}
    >
      {sections}
      {isCarousel && (
        <Box
          aria-label={`Click or press Enter to ${shouldPauseAnimation ? 'Play' : 'Pause'} animation`}
          css={theme => ({
            clip: 'rect(0 0 0 0)',
            clipPath: 'inset(50%)',
            position: 'absolute',
            zIndex: 10,
            right: 50,
            bottom: -10,
            backgroundColor: theme.colors.coolGrey[100],
            padding: '8px 12px',
            borderRadius: theme.radii.r4,
            width: '50px',
            display: 'flex',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:focus, &:focus-visible': {
              clip: 'auto',
              clipPath: 'none',
              outline: '5px solid',
              outlineOffset: 0,
              outlineColor: 'white',
              '&::before': {
                position: 'absolute',
                content: '""',
                top: '-6px',
                bottom: '-6px',
                left: '-6px',
                right: '-6px',
                borderStyle: 'solid',
                borderWidth: '2px',
                borderColor: theme.colors.coolGrey[100],
                borderRadius: theme.radii.r4,
              },
            },
          })}
          onClick={() => setShouldPauseAnimation(!shouldPauseAnimation)}
          onKeyDown={evt => {
            if (evt.key === 'Enter') {
              setShouldPauseAnimation(!shouldPauseAnimation);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <Icon color="white" type={shouldPauseAnimation ? 'Play' : 'Pause'} />
        </Box>
      )}
    </Box>
  );
};

export { LogoSection };
export default LogoSection;
